<template>
  <b-row class="MyRequests mb-5">
    <b-col cols="12" lg="10" offset-lg="1">
      <b-card>
        <b-card-title title-tag="h2" class="text-center">MY REQUESTS</b-card-title>

        <Notification v-bind="pendingNotifOptions" class="mb-5 rounded-0" />

        <b-nav tabs class="mb-1">
          <template v-for="(tab, idx) in tabList">
            <b-nav-item
              :key="`tab_${idx}`"
              :to="tab.link"
              :active="tab.active"
              :disabled="tab.disabled"
            >
              {{ tab.label }}
            </b-nav-item>
          </template>
        </b-nav>

        <!-- TODO: filter and search -->
        <Datatable
          :resources="resources"
          :options="datatableOptions"
          @update:perPage="updatePerPage"
          @updateSort="updateSort"
          @updatePage="updatePage"
        >
          <template v-slot:cell(actions)="scope">
            <span
              v-if="!$get(scope, `item.${requestViewField}`, false)"
              class="text-danger"
            >
              <v-icon name="exclamation-circle" />
              Error in record
            </span>
            <b-link
              v-else
              :to="{
                name: `${requestType}.view`,
                params: { [requestViewField]: $get(scope, `item.${requestViewField}`, '') },
              }"
            >
              View
            </b-link>
          </template>
        </Datatable>
      </b-card>
    </b-col>
  </b-row>
</template>
<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import Notification from '@/components/Notification';
import Datatable from '@/components/Datatable';
import overtimeStatuses from '@/mixins/overtimeStatuses';
import ResourceService from '@/services/ResourceService';

const resource = new ResourceService();

export default {
  name: 'MyRequests',
  components: {
    Notification,
    Datatable,
  },
  mixins: [overtimeStatuses],
  created() {
    this.getRequests();
  },
  data() {
    return {
      perPage: 10,
      page: 1,
      total: 0,
      sortBy: 'submitDate',
      sortDesc: true,
      listData: [],
    };
  },
  watch: {
    $route: 'getRequests',
  },
  computed: {
    pendingNotifOptions() {
      const tsCount = this.total;
      const forApprovalMessage = `${tsCount || 'No'} request${tsCount === 1 ? '' : 's'} found`;
      const loadingMessage = 'Loading your requests...';
      const icon = {
        name: this.isLoadingRequest ? 'circle-notch' : 'file-alt',
        spin: this.isLoadingRequest,
      };

      return {
        icon,
        show: true,
        message: this.isLoadingRequest ? loadingMessage : forApprovalMessage,
      };
    },
    requestType() {
      return this.$get(this.$route, 'meta.type', null);
    },
    requestViewField() {
      switch (this.requestType) {
        case 'timesheet':
          return 'code';
        case 'ob':
        case 'overtime':
          return 'key';
        default:
          return 'id';
      }
    },
    tabList() {
      return [
        {
          link: { name: 'requests.timesheet' },
          label: 'Timesheet',
          active: this.navIsActive('requests.timesheet'),
        },
        {
          link: { name: 'requests.ot' },
          label: 'Overtime',
          active: this.navIsActive('requests.ot'),
        },
        {
          link: { name: 'requests.ob' },
          label: 'Official Business',
          active: this.navIsActive('requests.ob'),
        },
      ];
    },
    datatableOptions() {
      return {
        small: true,
        pagination: true,
        dtControl: true,
        fields: this.tableFields || [],
        loading: this.isLoadingRequest,
        trClass: this.rowClass,
        page: this.page,
        perPage: this.perPage,
        total: this.total,
      };
    },
    tableFields() {
      const numField = {
        label: '#',
        key: 'id',
        class: 'text-center',
      };
      if (this.requestType === 'timesheet') {
        return [
          numField,
          {
            key: 'cutoffPeriod',
            label: 'Cutoff Period',
            sortable: true,
            class: 'text-center',
          },
          {
            label: 'Submit Date',
            key: 'submitDate',
            sortable: true,
            formatter: val => this.$fd(val, 'MMM DD, YYYY'),
            class: 'text-center',
          },
          {
            label: 'Status',
            key: 'status',
            sortable: true,
            tdClass: (val, key, item) => {
              let classes = 'text-center font-weight-bold';

              const statusID = this.$get(item, 'status_id', null);
              switch (statusID) {
                case this.approved:
                  classes += ' bg-success text-white';
                  break;
                case this.rejected:
                  classes += ' bg-danger text-white';
                  break;
                case this.cancelled:
                  classes += ' bg-secondary';
                  break;
                default:
                  break;
              }

              return classes;
            },
            formatter: this.$startCase,
          },
          {
            key: 'actions',
            label: 'Actions',
            sortable: false,
            class: 'text-center',
          },
        ];
      }
      return [
        numField,
        {
          sortable: true,
          class: 'text-center',
          label: 'Request Date',
          key: 'record_date',
          formatter: val => this.$fd(val, 'MMM DD YYYY'),
        },
        {
          label: 'Submit Date',
          key: 'submitDate',
          sortable: true,
          formatter: val => this.$fd(val, 'MMM DD YYYY - hh:mm A'),
          class: 'text-center',
        },
        {
          label: 'Status',
          key: 'status',
          sortable: true,
          tdClass: (val, key, item) => {
            let classes = 'text-center font-weight-bold';

            const statusID = this.$get(item, 'status_id', null);
            switch (statusID) {
              case this.approved:
                classes += ' bg-success text-white';
                break;
              case this.rejected:
                classes += ' bg-danger text-white';
                break;
              case this.cancelled:
                classes += ' bg-secondary';
                break;
              default:
                break;
            }

            return classes;
          },
          formatter: this.$startCase,
        },
        {
          label: 'Actions',
          key: 'actions',
          sortable: false,
          class: 'text-center',
        },
      ];
    },
    resources() {
      return (this.listData || []).map((ts) => {
        const otherData = {};
        const code = this.$get(ts, 'key', '');
        if (!code) {
          otherData.noCode = true;
        }
        return Object.assign({}, ts, otherData);
      });
    },
    ...mapGetters([
      'userData',
      'isLoadingRequest',
    ]),
    ...mapState(['notification']),
  },
  methods: {
    updateSort(payload) {
      this.sortBy = this.$get(payload, 'sortBy', null);
      this.sortDesc = this.$get(payload, 'sortDesc', false);
      this.getRequests();
    },
    updatePerPage(val) {
      this.perPage = val;
      this.page = 1;
      this.getRequests();
    },
    updatePage(val) {
      this.page = val;
      this.getRequests();
    },
    getRequests() {
      const userID = this.$get(this.userData, 'id', null);

      const otherParams = {
        perPage: this.perPage,
        page: this.page,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
      };

      this.startLoadingPage();
      this.startLoadingRequest();
      resource.get(`/${this.requestType}/list`, Object.assign({ userID }, otherParams))
        .then(({
          success,
          data,
          message,
          total,
        }) => {
          if (!success) {
            return this.setNotification({
              show: true,
              dismissible: true,
              variant: 'danger',
              message: message || 'An error occurred',
              icon: { name: 'exclamation-triangle' },
            });
          }
          this.listData = data;
          this.total = total;

          return success;
        })
        .catch(() => {
          this.setNotification({
            show: true,
            dismissible: true,
            variant: 'danger',
            message: 'An error occurred',
            icon: { name: 'exclamation-triangle' },
          });
        })
        .then(() => {
          this.stopLoadingRequest();
          this.stopLoadingPage();
        });
    },
    ...mapActions([
      'setNotification',
      'startLoadingRequest',
      'stopLoadingRequest',
      'startLoadingPage',
      'stopLoadingPage',
    ]),
  },
};
</script>
